import { useCallback, useEffect, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SectionCard, Field, Skeleton } from '../pipeline/ui';
import { formatCredits } from '../../lib/credits';

const EMPTY = { id: null, label: '', credits_per_cycle: '', price_usd: '', price_irr: '', active: true };

export function PlansEditor({ plans = [], onSave, onToggleActive }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const edit = (p) => { setForm({ id: p.id, label: p.label ?? '', credits_per_cycle: p.credits_per_cycle ?? '', price_usd: p.price_usd ?? '', price_irr: p.price_irr ?? '', active: p.active !== false }); setEditing(true); };
  const reset = () => { setForm(EMPTY); setEditing(false); };
  const submit = async () => {
    if (!String(form.label).trim()) { toast.error(t('plansAdmin.labelRequired')); return; }
    if (!(Number(form.credits_per_cycle) > 0)) { toast.error(t('plansAdmin.creditsRequired')); return; }
    setSaving(true);
    try { await onSave(form); toast.success(t('plansAdmin.saved')); reset(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <SectionCard title={t('plansAdmin.title')} testId="plans-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="plans-table">
          <thead>
            <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="text-start py-2 pe-4"><T k="plansAdmin.colLabel" /></th>
              <th className="text-start py-2 pe-4"><T k="plansAdmin.colCredits" /></th>
              <th className="text-start py-2 pe-4"><T k="plansAdmin.colPrice" /></th>
              <th className="text-start py-2 pe-4"><T k="plansAdmin.colActive" /></th>
              <th className="text-start py-2"></th>
            </tr>
          </thead>
          <tbody className="font-data">
            {plans.length === 0 ? (
              <tr><td colSpan="5" className="py-3 text-muted-foreground" data-testid="plans-empty">—</td></tr>
            ) : plans.map((p) => (
              <tr key={p.id} className={`border-b border-border ${p.active === false ? 'opacity-50' : ''}`} data-testid={`plan-row-${p.id}`}>
                <td className="py-2 pe-4 text-foreground">{p.label}</td>
                <td className="py-2 pe-4">{Number(p.credits_per_cycle).toLocaleString()}</td>
                <td className="py-2 pe-4">${Number(p.price_usd).toFixed(2)}</td>
                <td className="py-2 pe-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${p.active === false ? 'bg-muted text-muted-foreground border-border' : 'bg-success-soft text-success border-success-border'}`} data-testid={`plan-active-${p.id}`}>
                    {p.active === false ? t('plansAdmin.inactive') : t('plansAdmin.active')}
                  </span>
                </td>
                <td className="py-2 text-end whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => edit(p)} data-testid={`plan-edit-${p.id}`}><T k="plansAdmin.edit" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => onToggleActive(p.id, p.active === false)} data-testid={`plan-toggle-active-${p.id}`}>
                    {p.active === false ? t('plansAdmin.activate') : t('plansAdmin.deactivate')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-border pt-4 mt-2">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-semibold text-foreground" data-testid="plan-form-title">{editing ? t('plansAdmin.editPlan') : t('plansAdmin.addPlan')}</h4>
          {editing && <Button variant="ghost" size="sm" onClick={reset} data-testid="plan-cancel"><T k="plansAdmin.cancel" /></Button>}
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label={<T k="plansAdmin.colLabel" />}><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Pro" data-testid="plan-label" /></Field>
          <Field label={<T k="plansAdmin.colCredits" />}><Input type="number" value={form.credits_per_cycle} onChange={(e) => setForm({ ...form, credits_per_cycle: e.target.value })} data-testid="plan-credits" /></Field>
          <Field label={<T k="plansAdmin.colPrice" />}><Input type="number" step="1" value={form.price_irr} onChange={(e) => setForm({ ...form, price_irr: e.target.value })} data-testid="plan-price-irr" /></Field>
        </div>
        <div className="mt-4"><Button onClick={submit} disabled={saving} data-testid="plan-save">{saving ? t('plansAdmin.saving') : (editing ? t('plansAdmin.updatePlan') : t('plansAdmin.addPlan'))}</Button></div>
      </div>
    </SectionCard>
  );
}

// fixedEmail pins the card to one known user (the Manage popup): the email field disappears
// and the target cannot be retyped. Without it the card behaves as before — by email.
export function ApplyPlanCard({ plans = [], peg = 0.01, onApply, fixedEmail }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [planId, setPlanId] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const active = plans.filter((p) => p.active !== false);
  const target = (fixedEmail ?? email).trim();
  const canSubmit = !!target && !!planId;

  const submit = async () => {
    if (!canSubmit) return;
    setError(''); setResult(null); setBusy(true);
    try { const r = await onApply(target, planId); setResult(r); setEmail(''); setPlanId(''); toast.success(t('plansAdmin.applied')); }
    catch (e) { if (e?.message === 'user_not_found' || e?.status === 404) setError(t('plansAdmin.userNotFound')); else toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <SectionCard title={t('plansAdmin.applyTitle')} testId="plan-apply-card">
      <div className="flex flex-wrap items-end gap-3">
        {!fixedEmail && (
        <Field label={<T k="plansAdmin.applyEmail" />}>
          <Input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} placeholder="user@example.com" data-testid="apply-email" className="w-72" />
        </Field>
        )}
        <Field label={<T k="plansAdmin.applyPlan" />}>
          <select value={planId} onChange={(e) => setPlanId(e.target.value)} className="w-64 rounded-md border border-border px-3 py-2 text-sm bg-card" data-testid="apply-plan-select">
            <option value=""><T k="plansAdmin.applySelect" /></option>
            {active.map((p) => <option key={p.id} value={p.id}>{p.label} · {Number(p.credits_per_cycle).toLocaleString()} · {p.price_irr != null ? `${Number(p.price_irr).toLocaleString()} ${t('wallet.rial')}` : '—'}</option>)}
          </select>
        </Field>
        <Button onClick={submit} disabled={busy || !canSubmit} data-testid="apply-submit"><T k="plansAdmin.applyDo" /></Button>
      </div>
      {error && <p className="mt-3 text-sm text-destructive" data-testid="apply-error">{error}</p>}
      {result && <p className="mt-3 text-sm text-success" data-testid="apply-success">{t('plansAdmin.appliedTo')} <span className="font-data">{result.email}</span> · {formatCredits(result.wallet?.balance_credits, peg)}</p>}
    </SectionCard>
  );
}

export default function PlansAdmin() {
  const [plans, setPlans] = useState(null);
  const [peg, setPeg] = useState(0.01);
  const load = useCallback(() => {
    api.tenantAdmin('list_plans').then((r) => setPlans(r.plans || [])).catch((e) => toast.error(e.message));
    api.tenantAdmin('get_wallet_config').then((r) => { if (r.config?.credit_usd_peg) setPeg(Number(r.config.credit_usd_peg)); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSave = async (form) => {
    await api.tenantAdmin('upsert_plan', { id: form.id || undefined, label: form.label, credits_per_cycle: Number(form.credits_per_cycle), price_usd: Number(form.price_usd) || 0, price_irr: form.price_irr, active: form.active });
    load();
  };
  const onToggleActive = async (id, active) => { try { await api.tenantAdmin('set_plan_active', { id, active }); load(); } catch (e) { toast.error(e.message); } };

  if (plans === null) return <Skeleton className="h-40 w-full" />;
  return (
    <div className="flex flex-col gap-6">
      <PlansEditor plans={plans} onSave={onSave} onToggleActive={onToggleActive} />
      {/* Applying a plan to a user moved into the per-user Manage popup (Ali, 2026-08-03). */}
    </div>
  );
}
